import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ArrowLeft, ArrowDown, Gift,
  Shield,
} from "lucide-react";
import { Link } from "react-router-dom";
import EditableText from "@/components/EditableText";
const heroBg = "/hero-bg.webp";
const heroBgMobile = "/hero-bg-mobile.webp";

/* ══════════════════ MAIN COMPONENT ══════════════════ */
const HeroBanner = () => {
  const isMobile = useIsMobile();

  return (
    <section data-hero-ready className="relative overflow-hidden min-h-[520px] sm:min-h-[510px] md:min-h-[600px] lg:min-h-[725px] 2xl:min-h-[725px]">
      {/* ── Background ── */}
      <div className="absolute inset-0">
        {/* Hero image – serve smaller file on mobile */}
        <picture>
          <source media="(max-width: 768px)" srcSet={heroBgMobile} type="image/webp" />
          <img src={heroBg} alt="" width={1280} height={700} className="w-full h-full object-cover object-top scale-[1.02]" loading="eager" fetchPriority="high" decoding="async" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/15 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary/12 rounded-full blur-[200px] pointer-events-none" />

        {/* Static decorative elements (no animation) */}
        <div className="absolute top-0 left-0 w-80 h-80 bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-[150px] pointer-events-none" />

        {/* Desktop-only animated backgrounds */}
        {!isMobile && (
          <>
            {/* Hex dot pattern */}
            <div className="absolute inset-0 opacity-[0.1] pointer-events-none">
              <svg width="100%" height="100%">
                <pattern id="hero-hex" x="0" y="0" width="60" height="70" patternUnits="userSpaceOnUse">
                  <circle cx="30" cy="18" r="2.5" fill="hsl(var(--primary))" />
                  <circle cx="0" cy="52" r="2.5" fill="hsl(var(--accent))" />
                  <circle cx="60" cy="52" r="2.5" fill="hsl(var(--primary))" />
                </pattern>
                <rect width="100%" height="100%" fill="url(#hero-hex)" />
              </svg>
            </div>

            {/* Aurora lights — uses transform for composited animation */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--primary) / 0.2), transparent 35%, hsl(var(--accent) / 0.15) 55%, transparent 75%, hsl(217 91% 60% / 0.12))',
                backgroundSize: '400% 400%',
              }}
              animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Scan line — uses translateY instead of top */}
            <motion.div
              className="absolute inset-x-0 h-[2px] pointer-events-none top-0"
              style={{
                background: 'linear-gradient(90deg, transparent 5%, hsl(var(--primary) / 0.5) 30%, hsl(var(--primary) / 0.7) 50%, hsl(var(--primary) / 0.5) 70%, transparent 95%)',
              }}
              animate={{ y: ["0%", "5000%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />

            {/* Center glow pulse — composited (transform + opacity only) */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.08), transparent 70%)' }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
          </>
        )}
      </div>

      {/* ── Content ── */}
      <div className="container relative py-10 sm:py-20 lg:py-28 flex items-center min-h-[inherit] px-4 sm:px-6">
        <div className="max-w-2xl flex flex-col gap-2 sm:gap-3">
          <div className={isMobile ? "" : "animate-fade-in"}>
            {/* Heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white mb-3 leading-[1.08] tracking-tight"
              style={{ textShadow: "0 3px 12px rgba(0,0,0,0.5)" }}
            >
              <span className="block" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}>
                <EditableText contentKey="hero-title-line1" defaultText="NiceGift - מתנות מותאמות אישית" />
              </span>
              <span
                className="text-primary inline-block relative mt-1"
                style={{ textShadow: "0 0 60px hsl(var(--primary) / 0.9), 0 0 120px hsl(var(--primary) / 0.5), 0 4px 20px rgba(0,0,0,0.3)" }}
              >
                <EditableText contentKey="hero-discount" defaultText="ומקוריות לכל אירוע" />
              </span>
            </h1>
            {/* Subtitle */}
            <p
              className="text-white text-base sm:text-xl lg:text-2xl mb-5 sm:mb-7 max-w-lg leading-relaxed font-bold"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}
            >
              <EditableText contentKey="hero-subtitle" defaultText="מתנות ומוצרים מספקים ישראליים, במחיר אחד וברור." />
            </p>

            {/* CTA row */}
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/קטגוריות"
                className="inline-flex items-center gap-2 sm:gap-2.5 bg-primary/20 backdrop-blur-md text-white font-bold px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base transition-all relative overflow-hidden group border border-primary/40 hover:bg-primary/30 hover:border-primary/60 hover:scale-[1.03] active:scale-95"
                style={{ boxShadow: "0 0 25px hsl(var(--primary) / 0.35), 0 0 60px hsl(var(--primary) / 0.15), inset 0 1px 0 hsl(var(--primary) / 0.3)" }}
              >
                <span className="absolute inset-0 bg-gradient-to-l from-primary/10 via-transparent to-primary/5 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-500" />
                <span className="relative z-10">קנה עכשיו</span>
                <ArrowLeft size={18} className="relative z-10" />
              </Link>

              <Link
                to="/קטגוריות"
                className="inline-flex items-center gap-2 sm:gap-2.5 bg-white/8 backdrop-blur-md text-white font-semibold px-5 sm:px-7 py-3 sm:py-3.5 rounded-full text-xs sm:text-sm border border-white/15 hover:bg-white/15 hover:border-white/30 transition-all shadow-[0_0_20px_rgba(255,255,255,0.05),inset_0_1px_0_rgba(255,255,255,0.1)]"
              >
                <Gift size={16} className="drop-shadow-[0_0_4px_rgba(255,255,255,0.4)]" />
                כל הקטגוריות
              </Link>
            </div>

            {/* Stats row - desktop only */}
            <div className="hidden sm:flex items-center gap-2 sm:gap-3 mt-5 sm:mt-8 flex-wrap">
              <Link to="/קטגוריות" className="flex items-center gap-2 bg-black/40 rounded-2xl px-4 py-3 border border-white/12 text-white/70 text-xs shadow-lg hover:bg-black/60 hover:border-primary/30 hover:text-white transition-all cursor-pointer">
                <Gift size={14} className="text-accent" />
                <span className="font-medium">קטגוריות מוצרים מגוונות</span>
              </Link>

              <Link to="/אודות" className="hidden lg:flex items-center gap-2 bg-black/35 rounded-2xl px-4 py-3 border border-white/10 text-white/60 text-xs shadow-lg hover:bg-black/60 hover:border-primary/30 hover:text-white transition-all cursor-pointer">
                <Shield size={13} className="text-primary" />
                <span className="font-medium">קנייה מאובטחת</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll down arrow - desktop only */}
      {!isMobile && (
        <motion.button
          onClick={() => window.scrollBy({ top: window.innerHeight * 0.85, behavior: 'smooth' })}
          className="absolute bottom-8 sm:bottom-14 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 cursor-pointer bg-transparent border-none outline-none group"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          aria-label="גלול למטה"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            <div className="relative bg-primary/15 backdrop-blur-md rounded-full p-3 border border-primary/30 group-hover:bg-primary/25 group-hover:border-primary/50 transition-all duration-300 group-hover:scale-110"
              style={{ boxShadow: "0 0 20px hsl(var(--primary) / 0.25), 0 0 50px hsl(var(--primary) / 0.1), inset 0 1px 0 hsl(var(--primary) / 0.2)" }}
            >
              <ArrowDown size={20} className="text-white sm:w-6 sm:h-6 drop-shadow-[0_0_6px_hsl(var(--primary)/0.8)]" />
            </div>
          </motion.div>
        </motion.button>
      )}

      {/* Bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/60 to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroBanner;
